import { Paper, PaperProps } from '@mui/material';

export type TCardContainerProps = PaperProps & {
  /**
   * Opts out of the card's themed border, so the container renders exactly `0px none`.
   *
   * The border is on by default (see below), which every consumer except the customer Details
   * card wants: `IndividualDetails` cites the frozen design for why it stays borderless and passes
   * this through.
   */
  disableBorder?: boolean;
};

/**
 * CardContainer is a wrapper around MUI's Paper component that applies
 * standard card styling from the theme.
 *
 * By default, it applies:
 * - borderRadius: theme.card.borderRadius (Tier 3)
 * - padding: theme.card.padding (Tier 3)
 * - a themed 1px border: borderWidth: theme.card.borderWidth (Tier 3), borderStyle: 'solid',
 *   borderColor: theme.palette.divider. The shorthand was previously incomplete (borderWidth with
 *   no style or colour), which computes to `0px none` and never renders; it was completed here
 *   once every consumer had been checked for whether it wants the border the theme has always
 *   specified.
 *
 * These defaults can be overridden by passing sx prop or other Paper props, or the border
 * specifically opted out of via `disableBorder`.
 *
 * @example
 * ```tsx
 * <CardContainer>
 *   <Typography>Content goes here</Typography>
 * </CardContainer>
 * ```
 *
 * @example
 * // With custom styling
 * ```tsx
 * <CardContainer sx={{ backgroundColor: 'primary.main' }}>
 *   <Typography>Custom styled content</Typography>
 * </CardContainer>
 * ```
 */
export default function CardContainer({ sx, disableBorder, ...props }: TCardContainerProps) {
  return (
    <Paper
      {...props}
      sx={[
        (theme) => ({
          borderRadius: theme.card.borderRadius,
          padding: theme.card.padding,
          borderWidth: disableBorder ? 0 : theme.card.borderWidth,
          borderStyle: disableBorder ? 'none' : 'solid',
          borderColor: disableBorder ? 'transparent' : theme.palette.divider,
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
